import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  SnackbarApiErrorMessage,
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import { IconLibrary } from '@product-framework/index';
import DialogCloseButton from '@product-framework/components/Dialogs/DialogCloseButton';
import { formatUSPhoneNumber } from '@product-framework/components/FormFields/PhoneNumber';
import { toDeleteRowPreview } from '../lib';
import { useInsightContact } from './InsightContactContext';

function InsightContactDeleteDialog({
  action = null,
  deleteContact = null,
  onClose = () => undefined,
  commonParams,
}) {
  const { formatMessage } = useLocalization();
  const snackbar = useSnackbar();
  const { selectedRows, clearRowSelection, removeRowSelection } =
    useInsightContact();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { remove } = useDataProvider(provider);

  const [loading, setLoading] = useState(false);

  const contacts = useMemo(() => {
    if (action === 'delete' && deleteContact) {
      return [toDeleteRowPreview(deleteContact)];
    }

    if (action === 'bulkDelete') {
      return selectedRows;
    }

    return [];
  }, [action, deleteContact, selectedRows]);

  const open = contacts.length > 0;
  const isBulkDelete = action === 'bulkDelete' && contacts.length > 1;

  const onCloseDialog = useCallback(() => {
    if (!loading) {
      onClose();
    }
  }, [loading, onClose]);

  const onConfirmDelete = async () => {
    setLoading(true);

    try {
      const contactIds = contacts.map(contact => contact?.id);
      const response = isBulkDelete
        ? await remove(commonParams.bulkDeletePhonesUrl, {
            data: { notificationPhoneIds: contactIds },
          })
        : await remove(`${commonParams.phonesUrl}/${contactIds[0]}`);

      if (!response?.ok) {
        snackbar.showMessage({
          type: 'error',
          data: <SnackbarApiErrorMessage errors={response?.errors} />,
        });
        return;
      }

      if (isBulkDelete) {
        clearRowSelection();
        snackbar.showMessage({
          data: formatMessage(
            {
              id: 'SUCCESSFULLY_DELETED_COUNT_CONTACTS',
              defaultMessage: 'Successfully deleted {count} contact(s).',
            },
            { count: contactIds.length },
          ),
        });
      } else {
        removeRowSelection(contactIds);
        snackbar.showMessage({
          data: formatMessage({
            id: 'SUCCESSFULLY_DELETED_CONTACT',
            defaultMessage: 'Successfully deleted contact.',
          }),
        });
      }

      commonParams.refreshTable();
      onClose();
    } catch (err) {
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={[{ message: err.message }]} />,
      });
    } finally {
      setLoading(false);
    }
  };

  const title = isBulkDelete
    ? formatMessage({
        id: 'DELETE_SELECTED_CONTACTS',
        defaultMessage: 'Delete selected contacts',
      })
    : formatMessage({
        id: 'DELETE_CONTACT',
        defaultMessage: 'Delete contact',
      });

  const description = isBulkDelete
    ? formatMessage(
        {
          id: 'INSIGHT_CONTACT_BULK_DELETE_DESCRIPTION',
          defaultMessage:
            'Remove the following {count} contact(s)? This cannot be undone.',
        },
        { count: contacts.length },
      )
    : formatMessage({
        id: 'INSIGHT_CONTACT_SINGLE_DELETE_DESCRIPTION',
        defaultMessage: 'Remove the following contact? This cannot be undone.',
      });

  return (
    <Dialog open={open} onClose={onCloseDialog} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconLibrary.Delete color="error" />
        {title}
        <DialogCloseButton disabled={loading} onClose={onCloseDialog} />
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box textAlign="center">
            <CircularProgress size={30} />
            <Typography mt={1}>
              {formatMessage({
                id: 'DELETING_CONTACTS',
                defaultMessage: 'Deleting contact(s)...',
              })}
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
            <Box
              sx={{
                mt: 2,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
              }}>
              <TableContainer sx={{ maxHeight: 200, overflowY: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        {formatMessage({
                          id: 'PHONE_NUMBER',
                          defaultMessage: 'Phone Number',
                        })}
                      </TableCell>
                      <TableCell>
                        {formatMessage({
                          id: 'DESCRIPTION',
                          defaultMessage: 'Description',
                        })}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {contacts.map((contact, index) => (
                      <TableRow key={contact?.id ?? index}>
                        <TableCell>
                          {formatUSPhoneNumber(contact?.phoneNumber)}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>
                          {contact?.description}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </>
        )}
      </DialogContent>
      {!loading && (
        <DialogActions sx={{ justifyContent: 'center', my: 1 }}>
          <Button variant="contained" color="error" onClick={onConfirmDelete}>
            {formatMessage({
              id: 'DELETE',
              defaultMessage: 'Delete',
            })}
          </Button>
          <Button variant="outlined" onClick={onCloseDialog}>
            {formatMessage({
              id: 'CANCEL',
              defaultMessage: 'Cancel',
            })}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

export default InsightContactDeleteDialog;
